import type { ReactNode } from "react";
import { useHistory, useParams } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { useFiles } from "@/react/components/Spaces/CourseSpace/Shared/LessonFile/hooks/useFiles";
import { useCourseCustomLabels } from "@/react/components/Spaces/CourseSpace/Shared/useCourseCustomLabels";
import type { Space } from "@/react/types/Space";
import { Layout } from "@circle-react/components/shared/CustomizeUI";
import { ExitButton } from "@circle-react/components/shared/CustomizeUI/Header/ExitButton";
import { spaces } from "@circle-react/helpers/urlHelpers";
import { useSmScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { BookmarkButton } from "@circle-react-shared/Bookmarks/BookmarkButton";
import { IconButton } from "@circle-react-shared/uikit/HeaderV3/IconButton";
import { Typography } from "@circle-react-uikit/Typography";

export interface LessonHeaderProps {
  isCourseCommentsEnabled: boolean;
  isDripped: boolean;
  lesson: any;
  previewReleasedToggle: ReactNode;
  refetchLesson: () => void;
  shouldShowComments: boolean;
  shouldShowFiles: boolean;
  shouldShowTOC: boolean;
  space: Space;
  toggleComments: () => void;
  toggleFiles: () => void;
  toggleTOC: () => void;
}

interface Params {
  spaceSlug: string;
}

export const LessonHeader = ({
  isCourseCommentsEnabled,
  isDripped,
  lesson,
  previewReleasedToggle,
  refetchLesson,
  shouldShowComments,
  shouldShowFiles,
  shouldShowTOC,
  space,
  toggleComments,
  toggleFiles,
  toggleTOC,
}: LessonHeaderProps) => {
  const { files, isLoading: isLoadingFiles } = useFiles();
  const { translatedLabels } = useCourseCustomLabels();
  const shouldShowFilesButton =
    !isLoadingFiles && files.length > 0 && isDripped;

  const history = useHistory();
  const params = useParams<Params>();
  const isSmScreen = useSmScreenMediaQuery();

  const goBackToSpace = () =>
    history.push(spaces.show({ spaceSlug: params.spaceSlug }));

  const commentLabel = shouldShowComments
    ? t("courses.open_panel", { panel: t("courses.comments") })
    : t("courses.close_panel", { panel: t("courses.comments") });

  const tocLabel = shouldShowTOC
    ? t("courses.open_panel", { panel: t("courses.toc") })
    : t("courses.close_panel", { panel: t("courses.toc") });

  const filesLabel = shouldShowFiles
    ? t("courses.open_panel", { panel: t("courses.files") })
    : t("courses.close_panel", { panel: t("courses.files") });

  return (
    <Layout.Header className="flex items-center justify-between px-5 md:px-6">
      <div className="flex items-center gap-2">
        <ExitButton onClick={goBackToSpace} />
        <div
          className="flex items-center space-x-1"
          data-testid="course-header-actions"
        >
          <Typography.TitleSm as="h1" weight="semibold">
            {isSmScreen
              ? t("spaces.form.create.space_types.course_label")
              : space?.name}
          </Typography.TitleSm>
        </div>
      </div>
      <div
        className="flex items-center gap-4"
        data-testid="lesson-header-actions"
      >
        {previewReleasedToggle}
        <div className="flex items-center gap-2">
          {isCourseCommentsEnabled && (
            <IconButton
              onClick={toggleComments}
              ariaLabel={commentLabel}
              name="20-comment"
              iconSize={20}
              buttonSize={24}
              iconClassName="!text-dark"
              dataTestId="comment-lesson"
            />
          )}
          <IconButton
            onClick={toggleTOC}
            ariaLabel={tocLabel}
            name="20-checklist"
            iconSize={20}
            buttonSize={24}
            iconClassName="!text-dark"
          />
          {shouldShowFilesButton && (
            <IconButton
              onClick={toggleFiles}
              ariaLabel={filesLabel}
              name="course-files"
              iconSize={20}
              buttonSize={24}
              iconClassName="!text-dark"
            />
          )}
          {lesson && (
            <BookmarkButton
              bookmarkType="lesson"
              record={lesson}
              onSuccess={refetchLesson}
              className="!text-dark"
              tooltip={t("bookmarks.actions.add_by_type.lesson", {
                lessonSingular: translatedLabels.lesson.singular.toLowerCase(),
              })}
            />
          )}
        </div>
      </div>
    </Layout.Header>
  );
};
